// LF-S2: React Context wrapper around the project editing session.
// ProjectDetailPage adopts this provider in LF-S3.
import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import {
  createInitialSession,
  projectSessionReducer,
  isDirty,
  dirtyCount,
  serializeSession,
} from './projectSession';

const ProjectSessionContext = createContext(null);

const backupKey = (projectId) => `hrapp.project.session.${projectId}`;

export function ProjectSessionProvider({ initialSnapshot, initialState, children }) {
  // LF-S5: `initialState` seeds the reducer directly — used to restore a
  // backed-up (dirty) session from localStorage. Otherwise start fresh.
  const [state, dispatch] = useReducer(
    projectSessionReducer,
    initialState || createInitialSession(initialSnapshot),
  );

  const dirty = isDirty(state);
  const projectId = state.current.project?.id;

  // localStorage backup — written while dirty, removed once clean.
  useEffect(() => {
    if (projectId == null) return;
    const key = backupKey(projectId);
    try {
      if (dirty) localStorage.setItem(key, serializeSession(state));
      else localStorage.removeItem(key);
    } catch {
      /* storage full / unavailable — backup is best-effort */
    }
  }, [state, dirty, projectId]);

  const value = useMemo(
    () => ({ state, dispatch, isDirty: dirty, dirtyCount: dirtyCount(state) }),
    [state, dirty],
  );

  return (
    <ProjectSessionContext.Provider value={value}>
      {children}
    </ProjectSessionContext.Provider>
  );
}

export function useProjectSession() {
  const ctx = useContext(ProjectSessionContext);
  if (!ctx) throw new Error('useProjectSession must be used within ProjectSessionProvider');
  return ctx;
}

export default ProjectSessionContext;
